import { NavLink } from "react-router-dom";

import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/features/auth/useAuth";
import { useActiveEvent } from "@/features/event/useActiveEvent";
import { cn } from "@/lib/utils";
import { NAV } from "@/router/nav-config";
import { useNavBadges } from "./useNavBadges";

/**
 * Sidebar do painel — nova Arquitetura da Informação (5 grupos + "Visão
 * geral" fora de grupo, ver `router/nav-config.ts`). `onNavigate` é chamado
 * ao clicar num item (usado pelo `PanelLayout` para fechar o off-canvas no
 * mobile).
 *
 * Decisão de design: TODOS os grupos ficam sempre expandidos (sem
 * `Collapsible`/accordion). Com 12 itens em 5 grupos, a lista inteira cabe
 * numa viewport de desktop comum sem scroll — um accordion só adicionaria
 * cliques (teria que abrir o grupo antes de navegar em telas mais baixas) e
 * complicaria destacar o item ativo quando o grupo dele está fechado, sem
 * ganho real de espaço. Reavaliar se a lista crescer muito além de 12 itens.
 *
 * Não há modo colapsado/só-ícones ainda (fora do escopo deste MVP), mas a
 * estrutura já está pronta para isso: `NavItem.label` é uma string separada
 * do componente de ícone (`icon`), e `TooltipProvider` já está montado
 * globalmente (`app/providers.tsx`) — um modo colapsado futuro só precisa
 * trocar `<span>{entry.label}</span>` por um `<Tooltip>` quando colapsado,
 * sem mexer em `nav-config.ts`.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { isOrg } = useAuth();
  const { eventId } = useActiveEvent();
  const badges = useNavBadges(eventId);

  return (
    <div className="flex h-full flex-col overflow-y-auto py-3">
      <div className="mb-1 flex items-center gap-2 px-3 text-[0.95rem] font-bold text-fg">
        <span className="grid size-7 shrink-0 place-items-center rounded-md bg-primary text-xs font-extrabold text-primary-foreground">
          E
        </span>
        EVENTOS.AI
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 px-2" aria-label="Navegação principal">
        {NAV.map((entry, i) => {
          if ("group" in entry) {
            return (
              <div
                key={`group-${entry.group}-${i}`}
                className="mt-3 mb-1 px-2.5 text-[0.68rem] font-semibold tracking-wide text-fg-mute uppercase first:mt-1"
              >
                {entry.group}
              </div>
            );
          }

          // Gate de UX (defense-in-depth) — ver features/rbac/OrgOnly.tsx.
          if (entry.org && !isOrg) return null;

          const Icon = entry.icon;
          const badgeValue = entry.badgeKey ? badges[entry.badgeKey] : null;
          const isVisaoGeral = entry.key === "visao-geral";

          return (
            <div key={entry.key}>
              <NavLink
                to={entry.href}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-fg-soft transition-colors hover:bg-inset hover:text-fg",
                    isVisaoGeral && "font-semibold",
                    isActive && "bg-accent-weak text-accent-text hover:bg-accent-weak",
                  )
                }
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate">{entry.label}</span>
                {badgeValue ? (
                  <span className="rounded-full bg-accent-weak px-1.5 py-0.5 text-[0.7rem] font-semibold text-accent-text tabular-nums">
                    {badgeValue}
                  </span>
                ) : null}
              </NavLink>
              {isVisaoGeral ? <Separator className="mt-3 mb-1" /> : null}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
